import { IModel, IViolation, IAttribute } from 'app/model/base/annotation';
import { Metamodel, DATAFLOW_TEMPLATE_PROPERTY } from 'app/model/base/model';

import { IUsedIdentifiers } from './dataflow-expression-utilities';
import { Owned } from '../base/named';
import { IDataFlowDataType, AnyDataFlowType } from './dataflow-datatype';
import { DataFlowFunctionArgument } from 'app/model/dataflow/dataflow-function-argument';
import { IDataFlowExpression, ModelConstants, stringIsNullOrEmpty } from 'shell-models-lib';

@Metamodel.model({
    name: ModelConstants.dataflowExpression
})
export class DataFlowExpression extends Owned implements IDataFlowExpression {
    public static readonly MODEL: IModel;
    public static readonly EXPRESSION: IAttribute;

    @Metamodel.attribute({
        display: stringResources.Expression,
        association: 'EXPRESSION',
        // remove the required validation, because each grid has a default row with empty expression,
        // which will not be adapted. this will cause validation error and block user.
        constraint: {
            validator: DataFlowExpression.validateExpression
        }
    })
    public expression: string = '';
    public unserializedExpression: string = '';
    public isTemplatable: boolean;
    public ignoreAnyTypeValidation: boolean = false;

    public get templateExpression(): string {
        return DataFlowExpression.EXPRESSION.templateValue(this, DATAFLOW_TEMPLATE_PROPERTY);
    }

    public set templateExpression(expression: string) {
        DataFlowExpression.EXPRESSION.setTemplateValue(this, expression, DATAFLOW_TEMPLATE_PROPERTY);
    }

    public get argumentsUsed(): IUsedIdentifiers {
        return this._cachedIdentifiers;
    }

    public set argumentsUsed(argumentsUsed: IUsedIdentifiers) {
        this._cachedIdentifiers = argumentsUsed;
    }

    public get returnType(): IDataFlowDataType {
        return this._cachedReturnType || new AnyDataFlowType();
    }

    public set returnType(returnType: IDataFlowDataType) {
        this._cachedReturnType = returnType;
    }

    public get requiredFunctions(): DataFlowFunctionArgument[] {
        return this._requiredFunctions;
    }

    public get returnsCorrectType(): boolean {
        return !this.expression || !this._expectedReturnType || this._expectedReturnType === this.returnType.typeString || this._expectedReturnType === this.returnType.fullyQualifiedType;
    }

    public get hasUserDefinedFunction(): boolean {
        return !!this.argumentsUsed.dataFlowFunctions.find((func) => func.dataFlowFunction?.isUserDefinedFunction);
    }

    public get isUserDefinedFunction(): boolean {
        return this._isUserDefinedFunction;
    }

    public set isUserDefinedFunction(isUserDefinedFunction: boolean) {
        this._isUserDefinedFunction = isUserDefinedFunction;
    }

    public get userDefinedFunctionsUsed(): Set<string> {
        const usedFunctionNames = new Set<string>();
        this.argumentsUsed.dataFlowFunctions.forEach((func) => {
            if (func.dataFlowFunction?.isUserDefinedFunction) {
                usedFunctionNames.add(func.dataFlowFunction.name);
            }
        });
        return usedFunctionNames;
    }

    /**
     * Whether this DataFlowExpression is parameterized.
     */
    public get parameterized(): boolean {
        return !stringIsNullOrEmpty(this.templateExpression);
    }

    private _cachedIdentifiers: IUsedIdentifiers = {
        dataFlowColumns: [],
        dataFlowFunctions: [],
        dataFlowParameters: [],
        dataFlowCacheSinks: []
    };

    protected _expectedReturnType: string;
    protected _cachedReturnType: IDataFlowDataType;
    private _expressionHasError: boolean;
    private _badReferences: boolean;
    private _errors: string[] = [];
    private _warnings: string[] = [];
    private _missingRequiredFunctions: boolean = false;
    private _requiredFunctions: DataFlowFunctionArgument[];
    private _errorTextForMissingFunctions: string;
    private _isUserDefinedFunction: boolean = false;

    constructor() {
        super();
    }

    public static validateExpression(dataFlowExpression: DataFlowExpression): IViolation[] | undefined {
        if (!!dataFlowExpression.expression && (!dataFlowExpression.isTemplatable || dataFlowExpression.templateExpression)) {
            let ownerTransformation = dataFlowExpression.owner;
            while (ownerTransformation && !(Metamodel.modelOf(ownerTransformation).tag('isTransformation'))) {
                ownerTransformation = ownerTransformation.owner;
            }
            if (dataFlowExpression._expressionHasError) {
                return [
                    {
                        context: ownerTransformation,
                        message: () => stringResources.DataFlowExpressionHasError
                    }
                ];
            } else if (dataFlowExpression._badReferences) {
                return [
                    {
                        context: ownerTransformation,
                        message: () => stringResources.DataFlowExpressionUsesBadReferences
                    }
                ];
            } else if (dataFlowExpression._missingRequiredFunctions) {
                return [
                    {
                        context: ownerTransformation,
                        message: () => dataFlowExpression._errorTextForMissingFunctions
                    }
                ];
            }
        }

        return undefined;
    }

    public setRequiredFunctions(requriedFunctions: DataFlowFunctionArgument[], errorText: string): void {
        this._requiredFunctions = requriedFunctions;
        this._errorTextForMissingFunctions = errorText;
    }

    public get requiredReturnType(): string {
        return this._expectedReturnType;
    }

    public set requiredReturnType(expectedReturnType: string) {
        this._expectedReturnType = expectedReturnType;
    }

    public expressionHasError(): boolean {
        return this._expressionHasError || this._badReferences || this._missingRequiredFunctions;
    }

    public getExpressionErrors(): string[] {
        return this._errors;
    }

    public getExpressionWarnings(): string[] {
        return this._warnings;
    }

    public clearExpressionErrors(): void {
        this._errors = [];
        this._warnings = [];
        this._expressionHasError = false;
        this._badReferences = false;
        this._missingRequiredFunctions = false;
    }

    public updateExpressionErrorStatus(expressionHasError: boolean, badReferences: boolean, missingRequiredFunctions: boolean): void {
        this._expressionHasError = expressionHasError;
        this._badReferences = badReferences;
        this._missingRequiredFunctions = missingRequiredFunctions;
    }

    public addWarning(warningMessage: string): void {
        this._warnings.push(warningMessage);
    }

    public addError(errorMessage?: string): void {
        this._errors.push(errorMessage ? errorMessage : this._errorTextForMissingFunctions);
        this._expressionHasError = true;
    }
}

export function createDataflowExpression(expressionVal?: string, isTemplate: boolean = false): IDataFlowExpression {
    return {
        expression: expressionVal,
        isTemplatable: isTemplate,
        unserializedExpression: undefined,
        templateExpression: isTemplate ? expressionVal : undefined,
        ignoreAnyTypeValidation: true
    };
}
